type sortOrder = "ASC" | "DESC";

export const defaultSort = <T>(a: T, b: T, namespace: keyof T, order: sortOrder = "ASC") => {
  const value = order === "ASC" ? -1 : 1;
  if (a[namespace] === null) {
    return -value;
  } else if (b[namespace] === null) {
    return value;
  } else if (a[namespace] > b[namespace]) {
    return -value;
  } else if (a[namespace] < b[namespace]) {
    return value;
  }
  return 0;
};

export const numberSort = (a: string | number, b: string | number) => {
  if (!a) {
    if (!b) {
      return 0;
    }
    return -1;
  } else if (!b) {
    return 1;
  }
  const aValue = typeof a === "number" ? a : parseFloat(a);
  const bValue = typeof b === "number" ? b : parseFloat(b);
  if (aValue > bValue) {
    return 1;
  } else if (aValue < bValue) {
    return -1;
  }
  return 0;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const memorySort = <T extends { [key: string]: any }>(a: T, b: T, namespace: keyof T) => {
  const values = ["Byte", "kB", "MB", "GB"];
  const aSplitted = (a[namespace] as string).split(" ");
  const bSplitted = (b[namespace] as string).split(" ");
  const aValue = values.indexOf(aSplitted[1]);
  const bValue = values.indexOf(bSplitted[1]);
  if (aValue < bValue) {
    return -1;
  } else if (aValue > bValue) {
    return 1;
  } else {
    return numberSort(aSplitted[0], bSplitted[0]);
  }
};

export function stringSort<T>(a: T, b: T, namespace: keyof T, order: sortOrder = "ASC") {
  const value = order === "ASC" ? -1 : 1;
  if (`${a[namespace]}`.toLowerCase() < `${b[namespace]}`.toLowerCase()) {
    return value;
  }
  if (`${a[namespace]}`.toLowerCase() > `${b[namespace]}`.toLowerCase()) {
    return -value;
  }
  return 0;
}
const umlautMap = {
  "\u00dc": "UE",
  "\u00c4": "AE",
  "\u00d6": "OE",
  "\u00fc": "ue",
  "\u00e4": "ae",
  "\u00f6": "oe",
  "\u00df": "ss",
};

function replaceUmlaute(str) {
  return str
    .replace(/[\u00dc|\u00c4|\u00d6][a-z]/g, (a) => {
      const big = umlautMap[a.slice(0, 1)];
      return big.charAt(0) + big.charAt(1).toLowerCase() + a.slice(1);
    })
    .replace(new RegExp("[" + Object.keys(umlautMap).join("|") + "]", "g"), (a) => umlautMap[a]);
}

export function foreignKeyStringSort<T>(
  resolver,
  namespace: keyof T,
  additonalNamespaces: [keyof T],
  additonalResolverParam,
  order: sortOrder = "ASC"
) {
  return (a: T, b: T) => {
    const value = order === "ASC" ? 1 : -1;
    let ax = resolver(a[namespace], additonalResolverParam);
    let bx = resolver(b[namespace], additonalResolverParam);

    ax = replaceUmlaute(ax.toLowerCase());
    bx = replaceUmlaute(bx.toLowerCase());

    if (ax < bx) {
      return value;
    }
    if (ax > bx) {
      return value;
    }

    if (additonalNamespaces) {
      // only if ax and bx are equal, sort in additonalNamespaces
      ax = "";
      bx = "";
      additonalNamespaces.forEach((name) => {
        ax += "___" + a[name];
        bx += "___" + b[name];
      });
      ax = replaceUmlaute(ax.toLowerCase());
      bx = replaceUmlaute(bx.toLowerCase());
      if (ax < bx) {
        return value;
      }
      if (ax > bx) {
        return value;
      }
    }
    return 0;
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function dateSort<T extends { [key: string]: any }>(a: T, b: T, namespace: keyof T, order: sortOrder = "ASC") {
  try {
    const value = order === "ASC" ? -1 : 1;
    const aTime = new Date(Number(a[namespace])).getTime();
    const bTime = new Date(Number(b[namespace])).getTime();
    if (Number.isNaN(aTime)) {
      return 1 - Number(Number.isNaN(bTime));
    }
    if (aTime < bTime) {
      return value;
    }
    if (aTime > bTime) {
      return -value;
    }
    return 0;
  } catch (err) {
    alert("Parsing date error");
    throw err;
  }
}

export function compareValues<T extends object>(key: keyof T, order: "ASC" | "DESC" = "ASC") {
  return function innerSort(a, b) {
    if (!Object.prototype.hasOwnProperty.call(a, key) || !Object.prototype.hasOwnProperty.call(b, key)) {
      // property doesn't exist on either object
      return 0;
    }

    const varA = typeof a[key] === "string" ? a[key].toUpperCase() : a[key];
    const varB = typeof b[key] === "string" ? b[key].toUpperCase() : b[key];

    let comparison = 0;
    if (varA > varB) {
      comparison = 1;
    } else if (varA < varB) {
      comparison = -1;
    }
    return order === "DESC" ? comparison * -1 : comparison;
  };
}
